import * as angular from 'angular';
import { IStateService } from 'angular-ui-router';
import * as _ from 'lodash';
import * as moment from 'moment';
import { ImCourseDiff } from 'Src/ng2/shared/services/im-models/im-course-diff';
import { ImSchool } from 'Src/ng2/shared/services/im-models/im-school';
import { StudentStatic } from './../../../../../shared/static-models/student-static.service';

import { UtilitiesService } from 'src/ng2/shared/services/utilities/utilities.service';
import { CreditRequirements } from '../../../../../../ng2/shared/constants/credit-requirements.constant';
import { CsvExporterService } from '../../../../../../ng2/shared/services/csv-exporter/csv-exporter.service';
import { ImGapPlan } from '../../../../../../ng2/shared/services/im-models/im-gap-plan';
import { ICourseDiff } from '../../../../../../ng2/shared/typings/interfaces/course-diff.interface';
import { IGapPlan } from '../../../../../../ng2/shared/typings/interfaces/gap-plan.interface';
import { IUserMini } from '../../../../../../ng2/shared/typings/interfaces/user.interface';
import { SpinnerModal } from './../../../../../shared/modals/status-spinner/status-spinner.service';
import { ISdcStore } from './../../../../flux/sdc-store.interface';

type TRowType = 'ADD' | 'DROP';
type TRowStatus = 'PENDING' | 'APPLIED' | 'REMOVED' | 'INVALID' | 'PAST' | 'FUTURE';

interface IRow {
  studentId: string;
  studentName: string;
  createdAt: string;
  createdBy: string;
  courseOrplan: string;
  credits: number | string;
  gradReq: string;
  type: TRowType;
  term: string;
  termYear: string;
  status: TRowStatus;
  note?: string;
}

interface ICourseData {
  creditValue: number | string;
  gradReq: string;
}

export class CourseDiffsAndGapPlansTableCtrl {
  // bindings
  public courseDiffs: ICourseDiff[];
  public gapPlans: IGapPlan[];
  public currentSchool: any;

  // Available to template
  public filteredTable: IRow[];
  public isLoading: boolean;
  public studentFilter: string;
  public courseFilter: string;
  public gradReqFilter: string;
  public terms: {
    [termYear: number]: string;
  };
  public termYearFilter: string;
  // table sorting: set defaults
  public sortType = 'studentName';
  public sortReverse = false;
  public checkboxFilters = {
    courseAddition: {
      bool: true,
      column: 'type',
      value: 'ADD',
    },
    courseDrop: {
      bool: true,
      column: 'type',
      value: 'DROP',
    },
    removed: {
      bool: false,
      column: 'status',
      value: 'REMOVED',
    },
    applied: {
      bool: false,
      column: 'status',
      value: 'APPLIED',
    },
  };
  private currentTermYear: number;

  // Use to store all gapPlans/courseDiffs in proper format prior to any filtering
  private unfilteredTable: IRow[];

  static $inject = [
    'UtilitiesService',
    'CsvExporter',
    'SDCStore',
    '$scope',
    'FileSaver',
    'SpinnerModal',
    'Rollbar',
    '$state',
    '$window',
    'ImSchool',
    'ImGapPlan',
    'ImCourseDiff',
  ];

  constructor(
    private utils: UtilitiesService,
    private csvExporter: CsvExporterService,
    private SDCStore: ISdcStore,
    private $scope,
    private FileSaver: any,
    private spinnerModal: SpinnerModal,
    private Rollbar,
    private $state: IStateService,
    private $window,
    private imSchool: ImSchool,
    private imGapPlan: ImGapPlan,
    private imCourseDiff: ImCourseDiff,
  ) {}

  $onInit(): void {
    // table filtering:
    this.currentTermYear = this.imSchool.getCurrentTermYear(this.currentSchool);

    this.termYearFilter = this.currentTermYear.toString();

    // when store is updated build table:
    this.$scope.$listenTo(this.SDCStore, ['ui', 'isLoading'], () => {
      const isLoading = this.SDCStore.getUiState().isLoading;
      if (!isLoading) {
        this.setUnfilteredTable();
        this.setFilteredTable();
        _.sortBy(this.unfilteredTable, 'studentName');
        this.terms = this.getTerms(this.unfilteredTable);
      }
    });
  }

  buildTable(): IRow[] {
    let table = [];
    const gapPlanRows = this.getGapPlanRows();
    const courseDiffRows = this.getCourseDiffRows();
    table = [].concat(gapPlanRows, courseDiffRows);
    return table;
  }

  setUnfilteredTable(): void {
    const table = this.buildTable();
    this.unfilteredTable = table;
  }

  getGapPlanRows(): IRow[] {
    return _.map(this.gapPlans, gapPlan => {
      const {
        student: { studentId, lastFirst },
      } = gapPlan;
      const status = this.imGapPlan.getFrontendStatus(gapPlan, this.currentTermYear);
      const type: TRowType = 'ADD';
      return {
        _id: studentId,
        studentId: StudentStatic.getDistrictIdFromStudentId(studentId),
        studentName: lastFirst ? lastFirst : 'Student no longer active',
        createdAt: gapPlan.createdAt,
        createdBy: this.getCreatedByName(gapPlan.createdBy),
        courseOrplan: gapPlan.plan,
        credits: gapPlan.creditValue,
        gradReq: this.getHumanReadableGradReq(gapPlan.gradReq),
        type,
        term: gapPlan.termYear === 999 ? 'N/A' : this.utils.getHumanReadableTerm(gapPlan.termYear),
        termYear: gapPlan.termYear.toString(),
        status,
        note: gapPlan.note,
        show: true,
      };
    });
  }

  getCourseDiffRows() {
    return _.map(this.courseDiffs, courseDiff => {
      const {
        student: { studentId, lastFirst },
      } = courseDiff;
      const courseData = this.getCourseData(courseDiff.courseId);
      const status = this.imCourseDiff.getFrontendStatus(courseDiff);
      return {
        _id: studentId,
        studentId: StudentStatic.getDistrictIdFromStudentId(studentId),
        studentName: lastFirst ? lastFirst : 'Student no longer active',
        createdAt: courseDiff.createdAt,
        createdBy: this.getCreatedByName(courseDiff.createdBy),
        courseOrplan: courseDiff.courseId,
        credits: courseData.creditValue,
        gradReq: this.getHumanReadableGradReq(courseData.gradReq),
        type: courseDiff.action,
        term: courseDiff.termYear === 999 ? 'N/A' : this.utils.getHumanReadableTerm(courseDiff.termYear),
        termYear: courseDiff.termYear.toString(),
        status,
        show: true,
      };
    });
  }

  getCreatedByName(userMini: IUserMini): string {
    const { lastName, firstName } = userMini;
    return `${lastName},${firstName}`;
  }

  // needed since not all required table data is available on courseDiff:
  getCourseData(courseId: string): ICourseData {
    const course: any = _.find(this.currentSchool.masterSchedule, { courseId });
    if (!course) return { creditValue: '', gradReq: '' };
    return { creditValue: course.creditValue, gradReq: course.gradReq };
  }

  getHumanReadableGradReq(gradReq: string): string {
    const course: any = _.find(CreditRequirements, { camelCase: gradReq });
    if (course) return course.human;
  }

  createCsv(table: IRow[]): string {
    return this.csvExporter.createCourseDiffandGapPlanTableCsv(table);
  }

  // triggered in view via button:
  generateExport(): void {
    const docType = 'text/csv;charset=utf-8';
    const currentDateAndTime = moment().format('MM-DD-YYYY hmma');
    const fileName = `Program Change & Gap Plans ${currentDateAndTime}.csv`;
    const csvString = this.createCsv(this.filteredTable);
    this.csvExporter.exportCsv(csvString, fileName, docType);
  }

  // TABLE FILTRATION::
  autocompleteFilter(searchText: string) {
    return function filterFn(studentName) {
      return _.includes(studentName.toLowerCase(), searchText.toLowerCase());
    };
  }

  // called in md-autocomplete:
  getStudentNames(table: IRow[], searchText): string[] {
    const studentNames = _.uniq(_.map(table, row => row.studentName));
    const result = searchText ? _.filter(studentNames, this.autocompleteFilter(searchText)) : studentNames;
    return result;
  }

  getCourseNames(table: IRow[], searchText): string[] {
    const courseNames = _.uniq(_.map(table, row => row.courseOrplan));
    return searchText ? _.filter(courseNames, this.autocompleteFilter(searchText)) : courseNames;
  }

  getGradReqs(table: IRow[]): string[] {
    const gradReqs = _.uniq(
      _.reduce(
        table,
        (result, row) => {
          if (row.gradReq) result.push(row.gradReq);
          return result;
        },
        [],
      ),
    );
    return gradReqs.sort();
  }

  getTerms(table: IRow[]) {
    const terms = _.reduce(
      table,
      (result, row: IRow) => {
        if (!result[row.termYear]) result[row.termYear] = row.term;
        return result;
      },
      {},
    );
    _.sortBy(terms, 'termYear');
    return terms;
  }

  getCurrentTerm(currentTermYear: number): string {
    const humanReadable = this.utils.getHumanReadableTerm(currentTermYear);
    return humanReadable;
  }

  openStudentInSsv(studentId): void {
    const url = this.$state.href('main.school.single-student', { studentId });
    this.$window.open(url, '_blank');
  }

  resetFilter(): void {
    if (!this.unfilteredTable) return;
    this.spinnerModal.activateSpinner('Updating filter');
    // This is a bit of a hack, but the spinner modal wasn't opening
    // immediately on filter change until I moved setFilteredTable
    // into setTimeout. (JC)
    setTimeout(() => {
      this.setFilteredTable();
      this.spinnerModal.deactivateSpinner();
    }, 100);
  }

  setFilteredTable(): void {
    if (!this.unfilteredTable) {
      console.error(`Cannot call setFilteredTable before calling setUnFilteredTable`);
    }
    // Call each filter func with the table and pass
    // to next
    const filteredTable = _.filter(this.unfilteredTable, (row: IRow) => {
      // Include all rows by default
      let includeRow = true;

      includeRow = this.filterByTermYear(row);
      if (!includeRow) return includeRow;

      includeRow = this.filterByStudent(row);
      if (!includeRow) return includeRow;

      includeRow = this.filterByType(row);
      if (!includeRow) return includeRow;

      includeRow = this.filterByCourse(row);
      if (!includeRow) return includeRow;

      includeRow = this.filterByGradReq(row);
      if (!includeRow) return includeRow;

      // includeRow = this.filterByStatus(table);

      return includeRow;
    });

    this.filteredTable = filteredTable;
  }

  filterByTermYear(row: IRow): boolean {
    if (!this.termYearFilter) return true;
    return row.termYear === this.termYearFilter;
  }

  filterByStudent(row: IRow): boolean {
    if (!this.studentFilter) return true;
    return row.studentName === this.studentFilter;
  }

  filterByType(row: IRow): boolean {
    let includeRow = true;

    // We only care about filteres that are set to false, e.g. include === false
    const filters = _.filter(this.checkboxFilters, { bool: false });

    _.forEach(filters, filter => {
      const { column, value } = filter;
      const currVal = row[column];

      // If the current value is matches the filter value that should NOT be included
      // do not include this row, exit from forEach
      if (currVal === value) {
        includeRow = false;
        return;
      }
    });

    return includeRow;
  }

  filterByCourse(row: IRow): boolean {
    if (!this.courseFilter) return true;
    return row.courseOrplan === this.courseFilter;
  }

  filterByGradReq(row: IRow): boolean {
    if (!this.gradReqFilter) return true;
    return row.gradReq === this.gradReqFilter;
  }

  filterByStatus(row: IRow): boolean {
    return true;
  }
}
